import _ from "lodash";
import moment from "moment";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";

import {
  CAN_VIEW_ROUTINE_POSTS_FOR_CLASSES,
  CAN_MANAGE_ROUTINE_POSTS_FOR_CLASSES,
} from "Constants/permissionConstants";

export const ROUTINE_VALUE_STATUS_INDIVIDUAL = "INDIVIDUAL";
export const ROUTINE_VALUE_STATUS_ALL = "ALL";

export const updateAllRoutines = ({
  studentIds,
  currentStudentRoutines,
  key = [],
  value,
  studentTemplateIdMap,
  updateAllRoutinesValues = true,
  updateStudentValues = false,
}) => {
  const updateStudentRoutines = _.cloneDeep(currentStudentRoutines);
  const updatedValue = {
    ...value,
    status: ROUTINE_VALUE_STATUS_ALL,
  };

  !updateStudentValues &&
    _.setWith(updateStudentRoutines, ["ALL", ...key], updatedValue, Object);
  (updateAllRoutinesValues || updateStudentValues) &&
    _.forEach(studentIds, studentId => {
      _.setWith(
        updateStudentRoutines,
        [studentId, ...key],
        updatedValue,
        Object
      );
      _.setWith(
        updateStudentRoutines,
        [studentId, "studentTemplateResponseId"],
        studentTemplateIdMap[studentId],
        Object
      );
    });

  return updateStudentRoutines;
};

export const isRoutinesRoute = () => {
  return _.includes(window.location.href, "/routines");
};

export const updateSingleRoutine = ({
  studentIds,
  studentId,
  currentStudentRoutines,
  key = [],
  value,
  studentTemplateResponseId,
}) => {
  const updatedStudentRoutines = _.cloneDeep(currentStudentRoutines);
  // if user has set value same as common tab then mark its status as ALL rather than keeping it as individual.
  const updatedValue = {
    ...value,
    status: _.isEqual(
      _.get(updatedStudentRoutines, ["ALL", ...key, "value"]),
      _.get(value, "value")
    )
      ? ROUTINE_VALUE_STATUS_ALL
      : ROUTINE_VALUE_STATUS_INDIVIDUAL,
  };
  _.setWith(updatedStudentRoutines, [studentId, ...key], updatedValue, Object);
  _.setWith(
    updatedStudentRoutines,
    [studentId, "studentTemplateResponseId"],
    studentTemplateResponseId,
    Object
  );
  clearCommonRoutineData({
    studentIds,
    currentStudentRoutineData: updatedStudentRoutines,
    key,
  });
  return updatedStudentRoutines;
};

export const updateAllRoutineAttachments = ({
  studentIds,
  currentStudentRoutines,
  key = [],
  attachments,
}) => {
  const updatedStudentRoutines = _.cloneDeep(currentStudentRoutines);
  const updatedAttachments = _.map(attachments, attachment => ({
    value: attachment,
    status: ROUTINE_VALUE_STATUS_ALL,
  }));
  const commonAttachments = _.get(updatedStudentRoutines, ["ALL", ...key], []);
  _.setWith(
    updatedStudentRoutines,
    ["ALL", ...key],
    [...commonAttachments, ...updatedAttachments],
    Object
  );

  _.forEach(studentIds, studentId => {
    const currentStudentAttachments = _.get(
      updatedStudentRoutines,
      [studentId, ...key],
      []
    );
    _.setWith(
      updatedStudentRoutines,
      [studentId, ...key],
      _.map(
        [...currentStudentAttachments, ...updatedAttachments],
        (attachment, index) => ({
          value: { ...attachment.value, displaySequence: index + 1 },
          status: ROUTINE_VALUE_STATUS_ALL,
        })
      ),
      Object
    );
  });

  return updatedStudentRoutines;
};

export const updateSingleRoutineAttachments = ({
  studentId,
  currentStudentRoutines,
  key = [],
  attachments,
}) => {
  const updatedStudentRoutines = _.cloneDeep(currentStudentRoutines);
  const updatedAttachments = _.map(attachments, attachment => ({
    value: attachment,
    status: ROUTINE_VALUE_STATUS_INDIVIDUAL,
  }));
  const currentAttachments = _.get(
    updatedStudentRoutines,
    [studentId, ...key],
    []
  );
  _.setWith(
    updatedStudentRoutines,
    [studentId, ...key],
    [...currentAttachments, ...updatedAttachments],
    Object
  );

  return updatedStudentRoutines;
};

export const removeSingleRoutine = ({ studetnRoutineData, studentId }) => {
  if (!_.has(studetnRoutineData, studentId)) return studetnRoutineData; // If student ID doesn't exist, return unchanged studetnRoutineData.

  const studentProps = _.keys(_.get(studetnRoutineData, studentId, {}));
  const studentData = _.get(studetnRoutineData, studentId, {});

  _.unset(studetnRoutineData, studentId);

  // Check if any remaining students have the same properties
  _.forEach(studentProps, prop => {
    const allValue = _.get(studetnRoutineData, `ALL.${prop}.value`);

    const isPropUsedByOthers = _.some(
      studetnRoutineData,
      (student, id) =>
        id !== "ALL" && _.get(student, `${prop}.value`) === allValue
    );

    if (
      !isPropUsedByOthers &&
      _.get(studentData, `${prop}.value`) === allValue
    ) {
      _.unset(studetnRoutineData, `ALL.${prop}`);
    }
  });

  return studetnRoutineData;
};

export const removeSingleAttachmentFromRoutine = ({
  studentIds,
  studentId,
  currentStudentRoutines,
  key = [],
  attachmentToRemove,
}) => {
  const updatedStudentRoutine = _.cloneDeep(currentStudentRoutines);
  const updatedAttachmentToRemove = _.pick(attachmentToRemove, ["url"]);
  let commonAttachmentPresent = false;
  _.forEach(studentIds, currentStudentId => {
    if (currentStudentId === studentId) {
      const updatedAttachments = _.filter(
        _.get(updatedStudentRoutine, [currentStudentId, ...key], []),
        attachment =>
          !_.isEqual(
            _.pick(attachment?.value, ["url"]),
            updatedAttachmentToRemove
          )
      );
      _.setWith(
        updatedStudentRoutine,
        [currentStudentId, ...key],
        updatedAttachments,
        Object
      );
    }

    if (!commonAttachmentPresent) {
      const commonAttachment = _.find(
        _.get(updatedStudentRoutine, [currentStudentId, ...key], []),
        attachment =>
          _.isEqual(
            _.pick(attachment?.value, ["url"]),
            updatedAttachmentToRemove
          )
      );
      if (
        !_.isNil(commonAttachment) &&
        _.get(commonAttachment, "status") === ROUTINE_VALUE_STATUS_ALL
      )
        commonAttachmentPresent = true;
    }
  });

  // If attachment is added from common tab, and current student is last student having this attachment then remove it from ALL
  if (!commonAttachmentPresent) {
    const updatedAttachments = _.filter(
      _.get(updatedStudentRoutine, ["ALL", ...key], []),
      attachment =>
        !_.isEqual(
          _.pick(attachment?.value, ["url"]),
          updatedAttachmentToRemove
        )
    );
    _.setWith(
      updatedStudentRoutine,
      ["ALL", ...key],
      updatedAttachments,
      Object
    );
  }
  return updatedStudentRoutine;
};

export const removeAttachmentFromRoutines = ({
  studentIds,
  currentStudentRoutines,
  key = [],
  attachmentToRemove,
}) => {
  const updatedAttachmentToRemove = _.pick(attachmentToRemove, ["url"]);
  const updatedStudentActivities = _.cloneDeep(currentStudentRoutines);
  const updatedAttachments = _.filter(
    _.get(updatedStudentActivities, ["ALL", ...key], []),
    attachment =>
      !_.isEqual(_.pick(attachment?.value, ["url"]), updatedAttachmentToRemove)
  );
  _.setWith(
    updatedStudentActivities,
    ["ALL", ...key],
    updatedAttachments,
    Object
  );
  _.forEach(studentIds, studentId => {
    const updatedAttachments = _.filter(
      _.get(updatedStudentActivities, [studentId, ...key], []),
      attachment => {
        return !_.isEqual(
          _.pick(attachment?.value, ["url"]),
          updatedAttachmentToRemove
        );
      }
    );

    _.setWith(
      updatedStudentActivities,
      [studentId, ...key],
      updatedAttachments,
      Object
    );
  });

  return updatedStudentActivities;
};

export const formatTemplateResponseInput = ({
  studentIds = [],
  templateId,
}) => {
  return _.map(studentIds, studentId => ({
    templateId,
    studentId,
  }));
};

export const formatUpdateStudentTemplateResponseInput = (
  selectedRoutineTemplateId = "",
  studentIds,
  studentRoutineData,
  key,
  studentTemplateIdMap,
  value,
  readValue,
  updatedStudentRoutines,
  isAll = false
) => {
  const input = [];
  _.forEach(studentIds, student => {
    const storeValue = _.get(studentRoutineData, [student, key, "value"], "");
    const templateId = _.get(
      studentRoutineData,
      [student, "studentTemplateResponseId"],
      _.get(studentTemplateIdMap, student, "")
    );

    const updateValue = _.cloneDeep(value);

    if (readValue && isAll) {
      const attachments = _.get(updateValue, "addAttachmentGroups", []);

      const totalAttachments = _.size(_.get(updatedStudentRoutines, student));
      attachments.forEach((attachment, index) => {
        const newSequence = totalAttachments - index;
        attachment.displaySequence = newSequence;
        attachment.attachments.displaySequence = newSequence;
      });

      updateValue.addAttachmentGroups = attachments;
    }
    input.push({
      id: templateId ? templateId : studentTemplateIdMap[student],
      fields: [
        {
          fieldUid: key,
          ...(readValue ? updateValue : { value }),
        },
      ],
    });
  });

  return input;
};

export const formatCreatePostInput = studentTemplateIdMap => {
  const values = _.values(studentTemplateIdMap);
  const input = [];
  _.forEach(values, value => {
    input.push({
      parentId: value,
      parentType: "STUDENT_TEMPLATE_RESPONSE",
    });
  });
  return input;
};

export const getRoutinePostFeedFilters = ({
  userType = "",
  filters = {},
  userId,
}) => {
  const postFeedFilters = { ...filters };
  if (userType == "staff" && _.get(filters, "state") == "PUBLISHED") {
    postFeedFilters.states = ["PUBLISHED", "SCHEDULED"];
    postFeedFilters.state = undefined;
  }

  if (userType == "student" && _.get(filters, "state") == "PUBLISHED") {
    postFeedFilters.states = ["PUBLISHED", "IN_APPROVAL"];
    postFeedFilters.state = undefined;
  }

  if (userType == "student" && _.get(filters, "state") == "DRAFT") {
    postFeedFilters.studentIds = userId;
  }

  if (!_.isNil(postFeedFilters.duration)) {
    postFeedFilters.duration = !_.isNil(postFeedFilters.duration)
      ? {
          fromDate:
            !_.isNull(postFeedFilters?.duration?.fromDate) &&
            moment.isMoment(postFeedFilters?.duration?.fromDate)
              ? moment(
                  postFeedFilters?.duration?.fromDate.format("YYYY-MM-DD")
                ).toISOString()
              : postFeedFilters?.duration?.fromDate ?? null,
          toDate:
            !_.isNull(postFeedFilters?.duration?.toDate) &&
            moment.isMoment(postFeedFilters?.duration?.toDate)
              ? moment(postFeedFilters?.duration?.toDate.format("YYYY-MM-DD"))
                  .add({ hours: 23, minutes: 59 })
                  .toISOString()
              : postFeedFilters?.duration?.toDate ?? null,
        }
      : undefined;
  }
  postFeedFilters.feedTypes = "ROUTINE";
  return postFeedFilters;
};

export const getRoutinePostFeedFiltersMemoized = _.memoize(
  getRoutinePostFeedFilters,
  ({ userType, filters, userId }) => {
    const sortedFilters = _.pick(filters, _.sortBy(_.keys(filters)));
    return `${userType || ""}_${userId || ""}_${JSON.stringify(sortedFilters)}`;
  }
);

export const getFormattedStudentRoutineData = postDetails => {
  const postEditRoutineData = { ALL: {} };
  const allFields = new Set();
  const allAttachmentsMap = {}; // Track unique attachments per uid
  const uniqueAttachmentUrls = new Set(); // Track unique URLs globally

  // Identify fields that should be in "ALL"
  _.forEach(_.get(postDetails, "items", []), ({ item }) => {
    _.forEach(
      _.get(item, "fields", []),
      ({ uid, value, resolvedMinimalTree }) => {
        if (
          _.get(value, "status") === "ALL" ||
          _.some(_.get(resolvedMinimalTree, "attachmentGroups", []), group =>
            _.isEqual(_.get(group, "metadata.status"), "ALL")
          )
        ) {
          allFields.add(uid);
        }
      }
    );
  });

  // Build the postEditRoutineData object
  _.forEach(_.get(postDetails, "items", []), ({ item }) => {
    const studentId = _.get(item, "routineStudent.id");
    const studentTemplateResponseId = _.get(item, "id");

    if (!_.has(postEditRoutineData, studentId)) {
      _.set(postEditRoutineData, studentId, { studentTemplateResponseId });
    }

    _.forEach(
      _.get(item, "fields", []),
      ({ uid, value, resolvedMinimalTree }) => {
        let fieldData = null;

        if (!_.isNull(value)) {
          fieldData = {
            value: _.get(value, "value"),
            status: _.get(value, "status", null),
          };
        } else if (_.has(resolvedMinimalTree, "attachmentGroups")) {
          fieldData = _.flatMap(
            _.get(resolvedMinimalTree, "attachmentGroups", []),
            group =>
              _.map(_.get(group, "attachments", []), attachment => ({
                value: {
                  ...attachment,
                  attachmentGroupParentId: _.get(
                    group,
                    "metadata.attachmentGroupParentId",
                    ""
                  ),
                },
                status: _.get(group, "metadata.status", null),
              }))
          );
        }

        // Store in student-specific data
        _.set(postEditRoutineData, [studentId, uid], fieldData);

        // Store in ALL only if explicitly marked as "ALL"
        if (allFields.has(uid)) {
          if (_.get(value, "status") === "ALL") {
            _.set(postEditRoutineData, ["ALL", uid], {
              value: _.get(value, "value"),
              status: "ALL",
            });
          }

          if (_.has(resolvedMinimalTree, "attachmentGroups")) {
            if (!_.has(allAttachmentsMap, uid)) {
              _.set(allAttachmentsMap, uid, []);
            }

            _.forEach(
              _.get(resolvedMinimalTree, "attachmentGroups", []),
              group => {
                if (_.isEqual(_.get(group, "metadata.status"), "ALL")) {
                  _.forEach(_.get(group, "attachments", []), attachment => {
                    if (!uniqueAttachmentUrls.has(_.get(attachment, "url"))) {
                      uniqueAttachmentUrls.add(_.get(attachment, "url"));
                      allAttachmentsMap[uid].push({
                        value: {
                          ...attachment,
                          attachmentGroupParentId: _.get(
                            group,
                            "metadata.attachmentGroupParentId",
                            ""
                          ),
                        },
                        status: "ALL",
                      });
                    }
                  });
                }
              }
            );

            // Store unique attachments in ALL
            _.set(
              postEditRoutineData,
              ["ALL", uid],
              _.get(allAttachmentsMap, uid, [])
            );
          }
        }
      }
    );
  });

  return postEditRoutineData;
};

const clearCommonRoutineData = ({
  studentIds,
  currentStudentRoutineData,
  key,
}) => {
  let isAnyStudentContainCommonProp = false;
  for (const studentId of studentIds) {
    const status = _.get(currentStudentRoutineData, [
      studentId,
      ...key,
      "status",
    ]);
    if (
      _.has(currentStudentRoutineData, [studentId, ...key, "status"]) &&
      status === ROUTINE_VALUE_STATUS_ALL
    ) {
      isAnyStudentContainCommonProp = true;
      break;
    }
  }
  if (!isAnyStudentContainCommonProp)
    _.unset(currentStudentRoutineData, ["ALL", ...key]);
};

export const mapRoutineStudents = data => {
  return _.reduce(
    data,
    (acc, postItem) => {
      const routineStudentId = _.get(postItem, "item.routineStudent.id");
      const itemId = _.get(postItem, "item.id");

      if (routineStudentId && itemId) {
        acc[routineStudentId] = itemId;
      }
      return acc;
    },
    {}
  );
};

export const formatUpdateParentEntityInput = (
  oldPostDetails,
  postDetails,
  studentTemplateIdMap,
  oldStudentData
) => {
  // Get old and new tagged students
  const oldTaggedStudents = _.map(
    _.get(oldPostDetails, "students.edges", []),
    student => student.node.id
  );

  const newTaggedStudents = _.map(
    _.get(postDetails, "students.edges", []),
    student => student.node.id
  );

  const oldTaggedStudentsData = _.map(
    _.get(oldStudentData, "edges", []),
    student => student.node.id
  );

  const updatedOldTaggedStudents =
    _.size(oldTaggedStudents) > 0 ? oldTaggedStudents : oldTaggedStudentsData;

  // Find added and removed students
  const addedStudents = _.difference(
    newTaggedStudents,
    updatedOldTaggedStudents
  );
  const removedStudents = _.difference(
    updatedOldTaggedStudents,
    newTaggedStudents
  );

  // Create mapped StudentTemplateIdMap for added and removed students
  const addedParentEntity = formatCreatePostInput(
    _.pick(studentTemplateIdMap, addedStudents)
  );
  const removedParentEntity = formatCreatePostInput(
    _.pick(studentTemplateIdMap, removedStudents)
  );

  return {
    addedParentEntity,
    removedParentEntity,
  };
};

export const isRoutinesEnabled = () => {
  return ACLStore.can("EnableClassRoutines");
};

export const getTimeDurationString = (hours, minutes) => {
  const hoursStr = hours > 0 ? `${hours}h` : "";
  const minutesStr = minutes > 0 ? `${minutes}m` : "";
  const spacer = hours > 0 && minutes > 0 ? " " : "";

  return hoursStr + spacer + minutesStr;
};

export const canManageRoutinePosts = () => {
  return ACLStorage.getPermissionValue(CAN_MANAGE_ROUTINE_POSTS_FOR_CLASSES);
};

export const canViewRoutinePosts = () => {
  return ACLStorage.getPermissionValue(CAN_VIEW_ROUTINE_POSTS_FOR_CLASSES);
};

export const canManageRoutineActions = userType => {
  if (userType === "staff") {
    return canManageRoutinePosts();
  }

  //Student and Parent do not have manage routine permissions
  return false;
};
